import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Headset, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState('email'); // 'email' | 'otp'
  const [resetEmail, setResetEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetMaskedPhone, setResetMaskedPhone] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/agent/login`, {
        email: formData.email,
        password: formData.password,
      });
      // Phase 0 — agent token now in HttpOnly cookie set by /agent/login.
      // Note: requires backend /api/agent/login to also call _set_session_cookie.
      localStorage.setItem('mojo_agent_user', JSON.stringify(response.data.agent));
      toast.success('Login successful');
      navigate('/agent/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordOpen = () => {
    setResetStep('email');
    setResetEmail('');
    setResetOtp('');
    setResetNewPassword('');
    setResetConfirmPassword('');
    setResetMaskedPhone('');
    setShowForgotPassword(true);
  };

  const handleRequestResetOtp = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/agent/request-password-reset`, { email: resetEmail });
      setResetMaskedPhone(response.data.masked_phone || '');
      toast.success('OTP sent to your registered phone number');
      setResetStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetNewPassword !== resetConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(`${API}/agent/reset-password`, {
        email: resetEmail,
        otp: resetOtp,
        new_password: resetNewPassword,
      });
      toast.success('Password reset successful! Please log in with your new password.');
      setShowForgotPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B0C34] via-[#2D1854] to-[#1B0C34] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-[#F2B10C] rounded-full flex items-center justify-center">
            <Headset size={32} className="text-[#1B0C34]" />
          </div>
          <CardTitle className="text-2xl text-[#1B0C34]">Mojo Money</CardTitle>
          <CardDescription>Call Center Agent Login</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="agent@callcenter.co.za"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={handleForgotPasswordOpen}
                className="text-sm text-[#145539] hover:underline"
              >
                Forgot Password?
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#1B0C34]">Reset Password</DialogTitle>
            <DialogDescription>
              {resetStep === 'email' && 'Enter your agent email to receive an OTP on your registered phone.'}
              {resetStep === 'otp' && `Enter the OTP sent to ${resetMaskedPhone} and your new password.`}
            </DialogDescription>
          </DialogHeader>

          {resetStep === 'email' && (
            <form onSubmit={handleRequestResetOtp} className="space-y-4">
              <div>
                <Label htmlFor="reset_email">Email Address</Label>
                <Input
                  id="reset_email"
                  type="email"
                  placeholder="agent@callcenter.co.za"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                disabled={resetLoading}
              >
                {resetLoading ? <><Loader2 size={16} className="animate-spin mr-2" />Sending...</> : 'Send OTP'}
              </Button>
            </form>
          )}

          {resetStep === 'otp' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="reset_otp">OTP Code</Label>
                <Input
                  id="reset_otp"
                  placeholder="6-digit OTP"
                  maxLength={6}
                  value={resetOtp}
                  onChange={(e) => setResetOtp(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reset_new_pass">New Password</Label>
                <Input
                  id="reset_new_pass"
                  type="password"
                  placeholder="Min. 6 characters"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reset_confirm_pass">Confirm Password</Label>
                <Input
                  id="reset_confirm_pass"
                  type="password"
                  placeholder="Repeat new password"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                disabled={resetLoading}
              >
                {resetLoading ? <><Loader2 size={16} className="animate-spin mr-2" />Resetting...</> : 'Reset Password'}
              </Button>
              <button
                type="button"
                onClick={() => setResetStep('email')}
                className="w-full text-sm text-[#145539] hover:underline"
              >
                Back
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentLogin;
